/**
 * OC FAB Widget Options Context
 * Provides widget options throughout the component tree
 * Eliminates prop drilling for configuration options
 */

import { createContext, FC, ReactNode, useContext, useMemo } from 'react';

import { OcFabWidgetOptions } from '../../../types/oc';
import { DEFAULT_PREVIEW_STATE, OC_DEFAULT_UI_CONFIG } from '../config/ocDefaults';

/**
 * Options with defaults applied
 * All primitive values are required (no longer optional)
 */
export type OcFabWidgetOptionsWithDefaults = Omit<
    Required<OcFabWidgetOptions>,
    // Keep these as optional (no sensible defaults)
    | 'avatarUrl'
    | 'brandingText'
    | 'transcriptFooter'
    | 'loadingMessage'
    | 'renderMessage'
    | 'renderAdditionalActions'
    | 'renderAdditionalMessageUI'
    | 'theme'
    | 'onReady'
    | 'onError'
    | 'onAuthRequired'
    | 'onChatChange'
    | 'onPostDiscussionSuccess'
    | 'onFabClick'
    | 'onPanelToggle'
> &
    Pick<
        OcFabWidgetOptions,
        | 'avatarUrl'
        | 'brandingText'
        | 'transcriptFooter'
        | 'loadingMessage'
        | 'renderMessage'
        | 'renderAdditionalActions'
        | 'renderAdditionalMessageUI'
        | 'theme'
        | 'onReady'
        | 'onError'
        | 'onAuthRequired'
        | 'onChatChange'
        | 'onPostDiscussionSuccess'
        | 'onFabClick'
        | 'onPanelToggle'
    >;

/**
 * Context for OC FAB Widget options
 * Provides access to widget configuration throughout the component tree
 * All values with defaults are guaranteed to be defined
 */
const OcFabWidgetOptionsContext = createContext<OcFabWidgetOptionsWithDefaults>({
    ...OC_DEFAULT_UI_CONFIG,
    title: `Open ${OC_DEFAULT_UI_CONFIG.assistantName}`,
    inputPlaceholder: `Ask ${OC_DEFAULT_UI_CONFIG.assistantName}...`,
    previewState: DEFAULT_PREVIEW_STATE,
} as OcFabWidgetOptionsWithDefaults);

export interface OcFabWidgetOptionsProviderProps {
    options?: OcFabWidgetOptions;
    children: ReactNode;
}

/**
 * Provider component for OC FAB Widget options
 * Should wrap the entire widget (FAB button and chat panel)
 * Merges user-provided options with defaults
 */
export const OcFabWidgetOptionsProvider: FC<OcFabWidgetOptionsProviderProps> = ({
    options = {},
    children,
}) => {
    // Merge user options with defaults
    const mergedOptions = useMemo(
        () => {
            const assistantName = options.assistantName ?? OC_DEFAULT_UI_CONFIG.assistantName;
            const showChatHistory = options.showChatHistory ?? OC_DEFAULT_UI_CONFIG.showChatHistory;

            // Adjust panel dimensions based on whether history is shown
            const panelBaseWidth = options.panelBaseWidth ?? (showChatHistory ? 1000 : 800);
            const panelMinWidth = options.panelMinWidth ?? (showChatHistory ? 585 : 385);

            return {
                ...OC_DEFAULT_UI_CONFIG,
                ...options,
                // Compute title based on assistantName if not explicitly provided
                title: options.title ?? `Open ${assistantName}`,
                // Compute inputPlaceholder based on assistantName if not explicitly provided
                inputPlaceholder:
                    options.inputPlaceholder ?? `Ask ${assistantName}...`,
                // Ensure assistantName is set
                assistantName,
                // Ensure showChatHistory is set
                showChatHistory,
                // Set panel dimensions with history-aware defaults
                panelBaseWidth,
                panelMinWidth,
                // Set default preview state if not provided
                previewState: options.previewState ?? DEFAULT_PREVIEW_STATE,
                // Merge nested fabPosition object
                fabPosition: {
                    ...OC_DEFAULT_UI_CONFIG.fabPosition,
                    ...options.fabPosition,
                },
            };
        },
        [options],
    ) as OcFabWidgetOptionsWithDefaults;

    return (
        <OcFabWidgetOptionsContext.Provider value={mergedOptions}>
            {children}
        </OcFabWidgetOptionsContext.Provider>
    );
};

/**
 * Hook to access OC FAB Widget options
 * @returns Widget options object with all defaults applied
 */
export const useOcFabWidgetOptions = (): OcFabWidgetOptionsWithDefaults => {
    return useContext(OcFabWidgetOptionsContext);
};

